import isEqual from 'lodash/isEqual';
import { storedValue as storedDuplicateTicketBehavior } from '../hooks/useDuplicateTicketBehaviorField';
import { productsForOrganization } from '../hooks/useNetworkLinkDefaultsField';
import { storedValue as storedQuicksightDashboard } from '../hooks/useQuicksightDashboardField';
import { storedComplexity } from '../hooks/usePasswordComplexityField';
import { toIsoDate } from '../hooks/useSettingsForm';
import featureFlags from '../../../backbone/lib/feature_flags.json';
import ticketDispositions from '../../../backbone/lib/ticket_dispositions.json';
import { PRODUCT_OPTIONS } from '../components/NetworkLinkDefaultsSection';
const Features = require('@scoutfdc/common/lib/features');
function displayOrNull(value) {
    return value === null || value === undefined || value === '' ? 'NULL' : String(value);
}
function displayDate(value) {
    const isoDate = toIsoDate(value);
    if (!isoDate || isoDate.length !== 10 || !isoDate.includes('-')) {
        return 'NULL';
    }
    const [year, month, day] = isoDate.split('-');
    return `${month}/${day}/${year}`;
}
function addChangedLine(lines, label, oldValue, newValue) {
    if (oldValue !== newValue) {
        lines.push({ label, oldValue, newValue });
    }
}
function diffPasswordComplexity(org, form) {
    const stored = storedComplexity(org);
    const field = form.passwordComplexity;
    const lines = [];
    addChangedLine(lines, 'Enable Password Complexity', (stored === null || stored === void 0 ? void 0 : stored.enabled) ? 'Enabled' : 'Disabled', field.enabled ? 'Enabled' : 'Disabled');
    addChangedLine(lines, 'Min Length', displayOrNull(stored ? String(stored.minLength) : ''), displayOrNull(field.minLength));
    addChangedLine(lines, 'Min Numbers', displayOrNull(stored ? String(stored.minNumbers) : ''), displayOrNull(field.minNumbers));
    addChangedLine(lines, 'Min Symbols', displayOrNull(stored ? String(stored.minSymbols) : ''), displayOrNull(field.minSymbols));
    addChangedLine(lines, 'Min Lowercase', displayOrNull(stored ? String(stored.minLowercase) : ''), displayOrNull(field.minLowercase));
    addChangedLine(lines, 'Min Uppercase', displayOrNull(stored ? String(stored.minUppercase) : ''), displayOrNull(field.minUppercase));
    return lines.length > 0 ? { name: 'Password Complexity', lines } : null;
}
function duplicateTicketBehaviorLabel(value) {
    if (value === 'soft_stop') {
        return 'Soft Stop';
    }
    if (value === 'hard_stop') {
        return 'Hard Stop';
    }
    return 'Not Set';
}
function diffDuplicateTicketBehavior(org, form) {
    const oldValue = duplicateTicketBehaviorLabel(storedDuplicateTicketBehavior(org));
    const newValue = duplicateTicketBehaviorLabel(form.duplicateTicketBehavior.value);
    if (oldValue === newValue) {
        return null;
    }
    return {
        name: 'Duplicate Ticket Behavior',
        lines: [{ label: null, oldValue, newValue }],
    };
}
function diffNetworkLinkDefaults(org, form) {
    var _a, _b;
    const baseline = productsForOrganization(org);
    const lines = [];
    for (const product of PRODUCT_OPTIONS) {
        const wasSelected = baseline.includes(product.code);
        const isSelected = (_b = (_a = form.networkLinkDefaultProducts) === null || _a === void 0 ? void 0 : _a.includes(product.code)) !== null && _b !== void 0 ? _b : false;
        if (wasSelected !== isSelected) {
            lines.push({ label: product.label, oldValue: wasSelected ? 'Checked' : 'Unchecked', newValue: isSelected ? 'Checked' : 'Unchecked' });
        }
    }
    return lines.length > 0 ? { name: 'Network Link Default Products', lines } : null;
}
function diffQuicksightDashboard(org, form) {
    const field = form.quicksightDashboard;
    if (field.error) {
        return null;
    }
    const patch = field.toOptionsPatch();
    if (!Object.prototype.hasOwnProperty.call(patch, 'default_quicksight_dashboard')) {
        return null;
    }
    const oldValue = displayOrNull(storedQuicksightDashboard(org));
    const newValue = displayOrNull(patch.default_quicksight_dashboard);
    if (oldValue === newValue) {
        return null;
    }
    return {
        name: 'Default Quicksight Dashboard',
        lines: [{ label: 'Dashboard UUID', oldValue, newValue }],
    };
}
function optionsPatchChanged(org, patch, key) {
    var _a, _b, _c;
    if (!Object.prototype.hasOwnProperty.call(patch, key)) {
        return false;
    }
    return !isEqual((_a = patch[key]) !== null && _a !== void 0 ? _a : null, (_c = (_b = org.options) === null || _b === void 0 ? void 0 : _b[key]) !== null && _c !== void 0 ? _c : null);
}
function diffPlaceholderSection(name, changed) {
    if (!changed) {
        return null;
    }
    return {
        name,
        lines: [{ label: null, oldValue: 'Previous configuration', newValue: 'Updated configuration' }],
    };
}
function diffGeneralSettings(org, form, updates) {
    var _a;
    const lines = [];
    const push = (label, oldValue, newValue) => {
        addChangedLine(lines, label, oldValue, newValue);
    };
    push('Days of Historical Edits', displayOrNull(org.days_of_historical_edit == null ? '' : String(org.days_of_historical_edit)), displayOrNull(updates.days_of_historical_edit));
    push('Ticket Number Max Chars', displayOrNull(org.ticket_number_max_chars == null ? '' : String(org.ticket_number_max_chars)), displayOrNull(updates.ticket_number_max_chars));
    push('Last Day To Calculate', displayDate(toIsoDate((_a = org.options) === null || _a === void 0 ? void 0 : _a.calc_cutoff)), displayDate(form.calcCutoff));
    if (form.featureCodes.includes(Features.midstream)) {
        push('Ticket Closing Date', displayDate(toIsoDate(org.ticket_closing_date)), displayDate(form.ticketClosingDate));
    }
    push('Active Date', displayDate(toIsoDate(org.active_date)), displayDate(form.activeDate));
    push('Inactive Date', displayDate(toIsoDate(org.inactive_date)), displayDate(form.inactiveDate));
    push('Billing Status', displayOrNull(org.billing_status || ''), displayOrNull(form.billingStatus));
    return lines.length > 0 ? { name: 'General Settings', lines } : null;
}
function diffToggleCatalog(name, catalog, current, baseline, sortAlpha) {
    const items = catalog.filter((item) => item.display !== false);
    if (sortAlpha) {
        items.sort((a, b) => a.label.localeCompare(b.label));
    }
    const lines = [];
    for (const item of items) {
        const was = baseline.includes(item.code);
        const is = current.includes(item.code);
        if (was !== is) {
            lines.push({ label: item.label, oldValue: was ? 'ON' : 'OFF', newValue: is ? 'ON' : 'OFF' });
        }
    }
    return lines.length > 0 ? { name, lines } : null;
}
export function buildSettingsDiff(org, form, canViewAll) {
    if (!org) {
        return [];
    }
    const updates = form.getUpdates();
    const optionsPatch = updates.options && typeof updates.options === 'object'
        ? updates.options
        : {};
    const sections = [];
    const addSection = (section) => {
        if (section) {
            sections.push(section);
        }
    };
    addSection(diffGeneralSettings(org, form, updates));
    if (!canViewAll) {
        return sections;
    }
    addSection(diffToggleCatalog('Features', featureFlags, form.featureCodes, org.feature_codes || [], false));
    addSection(diffToggleCatalog('Ticket Dispositions', ticketDispositions, form.dispositionCodes, org.ticket_dispositions || [], true));
    if (form.featureCodes.includes(Features.quicksight)) {
        addSection(diffQuicksightDashboard(org, form));
    }
    if (form.featureCodes.includes(Features.well_tests)) {
        addSection(diffPlaceholderSection('Well Test Overview Columns', optionsPatchChanged(org, optionsPatch, 'well_test_overview_options')));
    }
    if (form.wellTestManagementColumns.enabled) {
        addSection(diffPlaceholderSection('Well Test Management', form.wellTestManagementColumns.hasUnsavedChanges));
    }
    if (form.featureCodes.includes(Features.oil_sales_and_inventory_queue)) {
        addSection(diffPlaceholderSection('Oil Inventory Attributes', optionsPatchChanged(org, optionsPatch, 'oil_inventory')));
    }
    if (form.featureCodes.includes(Features.well_tests)) {
        addSection(diffPlaceholderSection('Meter Allocation Config', optionsPatchChanged(org, optionsPatch, 'meter_allocation_config')));
    }
    addSection(diffDuplicateTicketBehavior(org, form));
    addSection(diffPlaceholderSection('Ticket Overview Columns', optionsPatchChanged(org, optionsPatch, 'ticket_overview_options')));
    if (!form.passwordComplexity.ssoLocked) {
        addSection(diffPasswordComplexity(org, form));
    }
    if (form.featureCodes.includes(Features.network_links)) {
        addSection(diffNetworkLinkDefaults(org, form));
    }
    return sections;
}
